import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Package, Swords, Users } from "lucide-react";

interface Version {
  version: string;
  file_size: number;
  checksum: string | null;
  created_at: string;
  updated_at: string;
  metadata: any;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(3)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(3)} MB`;
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString();
}

export function VersionViewer() {
  const [unitsVersions, setUnitsVersions] = useState<Version[]>([]);
  const [weaponsVersions, setWeaponsVersions] = useState<Version[]>([]);
  const [divisionsVersions, setDivisionsVersions] = useState<Version[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchVersions = async () => {
    setLoading(true);
    setError(null);
    try {
      const [unitsRes, weaponsRes, divisionsRes] = await Promise.all([
        fetch("/api/units/versions"),
        fetch("/api/weapons/versions"),
        fetch("/api/divisions/versions"),
      ]);

      if (!unitsRes.ok || !weaponsRes.ok || !divisionsRes.ok) {
        throw new Error("Failed to fetch versions");
      }

      const units = await unitsRes.json();
      const weapons = await weaponsRes.json();
      const divisions = await divisionsRes.json();

      setUnitsVersions(units);
      setWeaponsVersions(weapons);
      setDivisionsVersions(divisions);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVersions();
  }, []);

  const VersionList = ({ versions, type }: { versions: Version[]; type: "units" | "weapons" | "divisions" }) => {
    if (versions.length === 0) {
      return (
        <div className="text-muted-foreground text-sm py-4">
          No versions found for {type}
        </div>
      );
    }

    // Find latest version (first in list, since it's sorted by created_at DESC)
    const latestVersion = versions[0]?.version;

    return (
      <div className="space-y-2">
        {versions.map((version) => (
          <Card
            key={version.version}
            className={`p-4 ${
              version.version === latestVersion
                ? "border-primary border-2"
                : ""
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex flex-col">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-lg">{version.version}</span>
                    {version.version === latestVersion && (
                      <span className="text-xs bg-primary text-primary-foreground px-2 py-0.5 rounded">
                        Latest
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground mt-1">
                    <div>Size: {formatFileSize(version.file_size)}</div>
                    <div>Created: {formatDate(version.created_at)}</div>
                    {version.updated_at !== version.created_at && (
                      <div>Updated: {formatDate(version.updated_at)}</div>
                    )}
                  </div>
                </div>
              </div>
              {version.metadata && (
                <div className="text-xs text-muted-foreground">
                  {version.metadata.migrated && (
                    <div>Migrated from {version.metadata.original_file}</div>
                  )}
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-destructive mb-4">{error}</p>
        <Button onClick={fetchVersions} variant="outline">
          Retry
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Version History</h2>
        <Button onClick={fetchVersions} variant="outline" size="sm">
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <div>
          <div className="flex items-center gap-2 mb-4">
            <Users className="h-5 w-5" />
            <h3 className="text-xl font-semibold">Units</h3>
          </div>
          <VersionList versions={unitsVersions} type="units" />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <Swords className="h-5 w-5" />
            <h3 className="text-xl font-semibold">Weapons</h3>
          </div>
          <VersionList versions={weaponsVersions} type="weapons" />
        </div>

        <div>
          <div className="flex items-center gap-2 mb-4">
            <Package className="h-5 w-5" />
            <h3 className="text-xl font-semibold">Divisions</h3>
          </div>
          <VersionList versions={divisionsVersions} type="divisions" />
        </div>
      </div>
    </div>
  );
}

